"use client";
import {
  useState,
  useMemo,
  FocusEventHandler,
  FormEvent,
  MouseEvent,
  ChangeEvent,
  useRef,
  useEffect,
} from "react";

import { usePathname, useSearchParams } from "next/navigation";
import { Search, Close } from "@/components/icons";
import { cn } from "@/lib/utils";
import debounce from "@/utils/debounce";
import { useProductSearchQuery } from "./ProductSearchContextProvider";
import { useInView } from "react-intersection-observer";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import { getCorrelationID } from "@/utils/tagular/helpers";
import useWebview from "@/webview/useWebview";
import { sendAnalyticsProps } from "./index";
import { resolveClientMarketplaceAttribution } from "@/utils/functions/marketplaceAttributionSession";
import useTranslations from "@/i18n/useTranslations";
import { useNavigationMessages } from "@/i18n/hooks/useNavigationMessages";
import { useLocale } from "next-intl";
import { withLocalePrefix } from "@/i18n/withLocalePrefix";

interface SearchBoxProps {
  button?: string;
  searchButtonColor?: string;
  searchButtonLabelColor?: string;
  onFocus: FocusEventHandler<HTMLInputElement>;
  onBlur: FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  variant?: string;
  onSubmit?: (props: sendAnalyticsProps) => void;
  showButtons?: boolean;
  isRoundedButton?: boolean;
  isSearchBoxColumn?: boolean;
  autoFocusOnMount?: boolean;
  location?: string;
  /** Revamp/modal delegates magnifier Tagular events to ProductSearch parent. */
  suppressInternalSearchMagnifierAnalytics?: boolean;
}

/**
 * Renders a search box component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.onFocus - The onFocus event handler.
 * @param {Function} props.onBlur - The onBlur event handler.
 * @param {string} props.placeholder - The placeholder text for the search input.
 * @returns {JSX.Element} The rendered search box component.
 */
const SearchBox = ({
  searchButtonColor,
  searchButtonLabelColor,
  button,
  onFocus,
  onBlur,
  placeholder,
  variant = "",
  onSubmit = () => {},
  showButtons,
  isRoundedButton,
  isSearchBoxColumn,
  autoFocusOnMount,
  suppressInternalSearchMagnifierAnalytics = false,
  location = "embedded-search",
}: SearchBoxProps) => {
  const tSearch = useTranslations("Search");
  const locale = useLocale();
  const navMessages = useNavigationMessages();
  const resolvedButton = button ?? tSearch("searchButton");
  const resolvedPlaceholder = placeholder ?? navMessages.searchPlaceholder;
  const { query, setQuery, registerClearInput } = useProductSearchQuery();
  const [searchFocused, setSearchFocused] = useState(false);
  const [input, setInput] = useState<string>(query);

  useEffect(() => {
    registerClearInput(() => setInput(""));
    return () => registerClearInput(null);
  }, [registerClearInput]);
  const { isWebview } = useWebview();
  const pathname = usePathname();
  const currentSearchParams = useSearchParams();
  const pathSegments = pathname?.split("/") ?? [];
  const authxIndex = pathSegments.indexOf("authx");
  const isAuthX = authxIndex !== -1;
  const isNavHeaderVariant = variant === "nav-header";
  const isShopVariant = variant === "shop";
  const isRevampVariant = variant === "revamp";
  const inputRef = useRef<HTMLInputElement>(null);

  const hasViewedRef = useRef(false);

  const searchWebElement = {
    elementType: ElementType.Entry,
    htmlId: "search-button",
    location: location,
    name: `${location}-button`,
    text: "search",
  };

  const { elementViewed: searchViewed } = useElementEvents({
    correlationId: getCorrelationID(),
    webElement: searchWebElement,
  });

  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (suppressInternalSearchMagnifierAnalytics) return;
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      searchViewed(); // No arguments needed
    }
  }, [suppressInternalSearchMagnifierAnalytics, inView, searchViewed]);

  // debounce search query
  const debouncedSearch = useMemo(
    () =>
      debounce((searchQuery, search) => {
        search(searchQuery);
      }, 300),
    [],
  );

  // update local state for form control and trigger search
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const searchQuery = e.target.value;
    setInput(searchQuery);

    if (searchQuery.length >= 3) {
      debouncedSearch(searchQuery, setQuery);
    } else {
      setQuery("");
    }
  };

  // clear search query and reset form control
  const handleClear = (
    e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
  ) => {
    e.preventDefault();
    setInput("");
    setQuery("");
  };

  // submit search query and redirect
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const inputTrimmed = input?.trim();
    onSubmit({ input: inputTrimmed });

    const searchPath = isWebview
      ? "/webview/search"
      : isAuthX
        ? `${pathSegments.slice(0, authxIndex + 1).join("/")}/search`
        : withLocalePrefix("/search", locale);

    const searchParams =
      resolveClientMarketplaceAttribution(currentSearchParams);
    if (inputTrimmed.length) searchParams.set("q", inputTrimmed);
    const queryString = searchParams.toString();

    window.location.href = `${searchPath}${queryString ? `?${queryString}` : ""}`;
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus(e);
    if (isNavHeaderVariant || isRevampVariant) {
      setSearchFocused(true);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    onBlur(e);
    if (isNavHeaderVariant || isRevampVariant) {
      setSearchFocused(false);
    }
  };

  // Auto-focus input on mount (client-only)
  useEffect(() => {
    if (autoFocusOnMount && typeof window !== "undefined") {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [autoFocusOnMount]);

  // const handleSubmit
  return (
    <form
      data-testid="search-box"
      className={`flex overflow-hidden ${isSearchBoxColumn ? "flex-col" : ""} gap-2.5 md:flex-row md:gap-0`}
      onSubmit={(e) => handleSubmit(e)}
    >
      <div
        className={cn(
          "my-2 md:my-0 flex bg-white rounded min-h-12",
          isRevampVariant &&
            "border border-gray-500 rounded-default-md md:w-full",
          !isShopVariant && !isRevampVariant && "md:w-72",
          isNavHeaderVariant && "border border-gray-dark",
          (isNavHeaderVariant || isRevampVariant) &&
            (searchFocused || input?.trim().length) &&
            (isRevampVariant ? "md:w-[633px]" : "md:w-[504px]"),
        )}
      >
        <input
          ref={inputRef}
          type="search"
          className={cn(
            "border-0 grow focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-search-cancel-button]:hidden rounded w-[230px]",
            isNavHeaderVariant && "h-[44px] shadow-none",
            isRevampVariant &&
              "text-gray-500 text-sm placeholder:text-putty-600 placeholder:text-sm",
          )}
          value={input}
          onChange={(e) => handleChange(e)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={resolvedPlaceholder}
        />
        {showButtons && (
          <>
            <button
              ref={inViewRef}
              className={cn(
                (input?.trim().length ||
                  (isNavHeaderVariant && searchFocused)) &&
                  "hidden",
                "mx-4",
              )}
              type="submit"
              role="button"
              aria-label={tSearch("searchBoxAriaLabel")}
            >
              <Search
                pathProps={{
                  fill: isRevampVariant ? "#707070" : "currentColor",
                }}
              />
            </button>
            <button
              ref={inViewRef}
              className={cn(input?.trim().length ? "mx-4" : "hidden")}
              onClick={(e) => handleClear(e)}
              aria-label={tSearch("clearSearch")}
              title={tSearch("clearSearch")}
            >
              <Close
                pathProps={{
                  fill: isRevampVariant ? "#707070" : "currentColor",
                }}
              />
            </button>
          </>
        )}
      </div>
      {!isRevampVariant && (
        <button
          style={{
            backgroundColor: searchButtonColor,
            color: searchButtonLabelColor,
          }}
          className={cn(
            `${isRoundedButton ? "rounded-full" : ""} text-white text-base no-underline hover:no-underline bg-brand ml-0 md:ml-2 px-[1rem] py-[0.5625rem] border border-brand hover:bg-white hover:text-brand`,
            isNavHeaderVariant &&
              "bg-primary border-primary hover:text-white hover:border-secondary hover:bg-secondary",
            isNavHeaderVariant &&
              !searchFocused &&
              !input?.trim().length &&
              "md:hidden",
          )}
          type="submit"
        >
          {resolvedButton}
        </button>
      )}
    </form>
  );
};

export default SearchBox;
